import { URLSearchParams } from 'url';

import { isEmpty } from 'lodash-es';
import {
  ClientContactDTO,
  ClientDTO,
  findLegalIdentifierById,
  ILegalIdentifierSettings,
  amplitude,
  LogContextType,
  LogEntityType,
  LogAction,
  saveActivityLog,
} from 'wisestep-api';
import { formatLocation, WsSelectObj } from 'wisestep-base';

import { IBreadCrumbsList } from '../../../components/WsBreadCrumbs';
import getDecodedQueryParams from '../../../utils/getDecodedQueryParams';
import { activityLogDTOHelper } from '../../activityLog/ActivityHelper';
import { getTimezones } from '../../emailSettings/util/TimezoneHelper';
import { getJobIndustries } from '../../jobs/helper';

export const clientDetailsTabs = [
  {
    tabName: 'Company Info',
    tabCount: 0,
  },
  {
    tabName: 'Legal Identifiers',
    tabCount: 0,
  },
  {
    tabName: 'Bank Details',
    tabCount: 0,
  },
  {
    tabName: 'Our Entities',
    tabCount: 0,
  },
  {
    tabName: 'Client Contacts',
    tabCount: 0,
  },
  {
    tabName: 'Documents',
    tabCount: 0,
  },
  {
    tabName: 'Fee Arrangements',
    tabCount: 0,
  },
  {
    tabName: 'Analytics',
    tabCount: 0,
  },
  {
    tabName: 'Invoices',
    tabCount: 0,
  },
  {
    tabName: 'Candidates Placed',
    tabCount: 0,
  },
  {
    tabName: 'Positions',
    tabCount: 0,
  },
];

export interface IClientDetailsTabs {
  clientId: string;
  entityType?: 'client' | 'contact';
  country?: string;
  clientLegalName?: string;
  clientName?: string;
  clientContactId?: string;
}
export type ClientDetailsParams = {
  id: string;
};

export interface IPageState {
  pageNo: number;
  totalSize: number;
}

export const defaultPageState: IPageState = {
  pageNo: 0,
  totalSize: 0,
};

export const getLegalIdentifiersById = async (id: string | null) => {
  if (id) {
    const response = await findLegalIdentifierById(id);
    return response.data as ILegalIdentifierSettings;
  }
};
const jobIndustries = getJobIndustries();

export const jobIndustry = (value: string) =>
  jobIndustries.find((obj) => obj.value == value)?.label ?? '';

export const getCompanyInfoDetailsArray = (company: ClientDTO) => {
  const detailsArray: WsSelectObj[] = [];

  detailsArray.push({ label: 'Legal Name', value: company?.legalName ?? 'Not Available' });
  detailsArray.push({
    label: 'Location',
    value: formatLocation(company.location),
  });
  detailsArray.push({
    label: 'Time Zone',
    value: getCountryLabelByTzCode(company?.timezone) ?? 'Not Available',
  });
  detailsArray.push({ label: 'Branch', value: company?.branch ?? 'Not Available' });
  detailsArray.push({
    label: 'Industry Type',
    value: company?.industry ? jobIndustry(company?.industry) : 'Not Available',
  });
  detailsArray.push({ label: 'Source', value: company?.source ?? 'Not Available' });

  detailsArray.push({
    label: 'Payment Terms',
    value: company?.paymentTerms ? `${company.paymentTerms}${' days'}` : 'Not Available',
  });

  return detailsArray;
};

const getCountryLabelByTzCode = (tzCode: string): string =>
  getTimezones().find((tz) => tz.tzCode === tzCode)?.label ?? '';

export const fetchNameAndLinkedUrlsOfClientContact = (contact: ClientContactDTO) => {
  let name = contact?.firstName ?? '';
  if (!contact.lastName && contact.firstName) {
    name = contact.firstName;
  } else if (contact.lastName && contact.firstName) {
    name = `${contact.firstName} ${contact.lastName}`;
  } else if (contact.lastName) {
    name = `${contact.lastName}`;
  }
  if (contact.linkedinProfile) name = `${name}`;
  return name;
};

export const clientLayoutTabNames = (clienContactcount: number, childClientsCount: number) => [
  {
    tabName: 'Analytics',
  },
  {
    tabName: 'Activity',
  },
  {
    tabName: 'Child Company',
    tabCount: childClientsCount,
  },
  {
    tabName: 'Company Information',
  },
  {
    tabName: 'Documents',
  },
  {
    tabName: 'Contacts',
    tabCount: clienContactcount,
  },
  {
    tabName: 'Leads',
  },
  {
    tabName: 'CRM Jobs',
  },
];

export const filteredTabNames = (
  isChild: boolean,
  clienContactcount: number,
  childClientsCount: number
) => {
  return clientLayoutTabNames(clienContactcount, childClientsCount).filter((tab) => {
    if (!isChild && tab.tabName === 'Child Clients') {
      return false;
    }
    return true;
  });
};

export const clientContactLayoutTabNames = () => [
  {
    tabName: 'Analytics',
  },
  {
    tabName: 'Details',
  },
  {
    tabName: 'Activity',
  },
  {
    tabName: 'Documents',
  },
  {
    tabName: 'Leads',
  },
];

export const clientsAmplitudeEvents = (name: string, clickedOnName: string, workspace) => {
  amplitude(name, {
    clickedOn: clickedOnName,
    workspaceName: workspace.name,
    pageName: 'Clients',
    planType: workspace.planType,
    interfaceType: 'web',
    pageUrl: window.location.href,
  });
};

const baseCrumbs = {
  CLIENTS: [{ label: 'Company', url: '/clients/' }],
  CLIENT_CONTACTS: [{ label: 'Company Contacts', url: '/contacts-list' }],
  ORGANIZATION_ENTITY: [{ label: 'Organization Entity', url: '/organisationEntity/list/' }],
};

export type IClientBreadCrumbTypes = keyof typeof baseCrumbs | 'ADD_CLIENT';
export const ClintPageBreadCrumb: Record<IClientBreadCrumbTypes, IBreadCrumbsList[]> = {
  ...baseCrumbs,
  ADD_CLIENT: [...baseCrumbs.CLIENTS, { label: 'Add Company', url: '/clients/addclientoptions' }],
};

export const getClientsBreadCrumbList = (
  type: IClientBreadCrumbTypes,
  label: string
): IBreadCrumbsList[] => [
  ...ClintPageBreadCrumb[`${type}`],
  { label, url: `${window.location.pathname}${window.location.search}` },
];

export const getClientOrContactListBreadCrumbs = (
  param: URLSearchParams,
  label = '',
  defaultType: 'CLIENTS' | 'CLIENT_CONTACTS'
) => [
  ...((!isEmpty(param.get('breadCrumbList'))
    ? getDecodedQueryParams(param.get('breadCrumbList') ?? '')
    : ClintPageBreadCrumb[defaultType]) as IBreadCrumbsList[]),
  { label, url: `${window.location.pathname}${window.location.search}` },
];

export const limitedPrivacyOptions = new Map<string, string>();
limitedPrivacyOptions.set('Open for Communication', 'open');
limitedPrivacyOptions.set('Right to Restrict', 'restrict');

export const createClientContactOwnerActivityLog = async (
  oldData: ClientContactDTO,
  updatedContact: ClientContactDTO
) => {
  if (oldData) {
    oldData.owner = updatedContact.owner;
    const oldOwner = oldData.owner ? oldData.owner.name : 'none';
    const newOwner = updatedContact.owner?.name;
    const logPayload = {
      ...activityLogDTOHelper,
      id: updatedContact.id,
      action: LogAction.Edit,
      field: 'Client Contact Owner',
      description: `updated Client Contact Owner from ${oldOwner} to ${newOwner} for ${updatedContact.firstName}`,
      entityId: updatedContact.id || '',
      entityName: updatedContact.firstName,
      entityType: LogEntityType.ClientContact,
      parentEntitiyType: LogContextType.Client,
      contextData: { clientId: updatedContact.clientId },
      parentEntitiyId: updatedContact.clientId,
    };
    await saveActivityLog(logPayload);
  }
};
